import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

export default function TemplatesView({ isSuperAdmin, logAction }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewArchived, setViewArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const initialForm = { name: '', type: 'File', textContent: '', fileUrl: '', fileName: '', status: 'Active', category: 'General' };
  const [formData, setFormData] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Viewing State
  const [viewingTemplate, setViewingTemplate] = useState(null);

  // --- Listeners ---
  useEffect(() => {
    const q = query(collection(db, "templates"), orderBy("name"));
    const unsub = onSnapshot(q, (snap) => {
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error loading templates:", error);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // --- Handlers ---
  const handleFileChange = (e) => {
    if (e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) return;
    setUploading(true);

    try {
      let fileData = { fileUrl: formData.fileUrl, fileName: formData.fileName };

      // Upload File if selected and type is File
      if (formData.type === 'File' && selectedFile) {
        const storageRef = ref(storage, `templates/${Date.now()}_${selectedFile.name}`);
        await uploadBytes(storageRef, selectedFile);
        const url = await getDownloadURL(storageRef);
        fileData = { fileUrl: url, fileName: selectedFile.name };
      }

      const finalData = {
        ...formData,
        ...fileData,
        updatedAt: new Date().toISOString()
      };

      if (isEditing) {
        await updateDoc(doc(db, "templates", editingId), finalData);
        logAction("Update Template", `Updated template: ${formData.name}`);
      } else {
        await addDoc(collection(db, "templates"), {
          ...finalData,
          createdAt: new Date().toISOString()
        });
        logAction("Create Template", `Created template: ${formData.name}`);
      }
      closeModal();
    } catch (err) {
      console.error(err);
      alert("Error saving template.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(`Permanently delete template "${name}"?`)) return;
    try {
      await deleteDoc(doc(db, "templates", id));
      logAction("Delete Template", `Deleted ${name}`);
    } catch (err) { console.error(err); }
  };

  const handleArchive = async (id, currentStatus) => {
    if (!isSuperAdmin) return;
    const newStatus = currentStatus === 'Active' ? 'Archived' : 'Active';
    try {
      await updateDoc(doc(db, "templates", id), { status: newStatus });
    } catch (err) { console.error(err); }
  };

  const openAddModal = (tpl = null) => {
    if (tpl) {
      setFormData(tpl);
      setEditingId(tpl.id);
      setIsEditing(true);
    } else {
      setFormData(initialForm);
      setIsEditing(false);
    }
    setSelectedFile(null);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowTextModal(false);
    setViewingTemplate(null);
  };

  const openTextView = (tpl) => {
    setViewingTemplate(tpl);
    setShowTextModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  // --- Render ---
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) || t.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = viewArchived ? t.status === 'Archived' : t.status === 'Active';
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="container-fluid p-0">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-main">Templates</h2>
          <p className="text-muted-theme">Standardized documents and text snippets.</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <input 
            type="text" 
            className="form-control" 
            placeholder="Search templates..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
          <div className="btn-group">
             <button className={`btn ${!viewArchived ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewArchived(false)}>Active</button>
             <button className={`btn ${viewArchived ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewArchived(true)}>Archived</button>
          </div>
          {isSuperAdmin && (
            <button className="btn btn-primary" onClick={() => openAddModal()}>
              <i className="bi bi-plus-lg"></i> Add
            </button>
          )}
        </div>
      </div>

      {/* GRID */}
      {loading ? <div className="text-main">Loading...</div> : (
        <div className="row g-4">
          {filteredTemplates.length === 0 && (
             <div className="col-12 text-center text-muted-theme py-5">
                <i className="bi bi-folder2-open fs-1 d-block mb-3"></i>
                No {viewArchived ? 'archived' : 'active'} templates found.
             </div>
          )}

          {filteredTemplates.map(t => (
            <div key={t.id} className="col-md-6 col-xl-4">
              <div className={`card h-100 bg-card border-theme shadow-sm ${t.status === 'Archived' ? 'opacity-50' : ''}`}>
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className={`rounded-circle p-3 d-flex align-items-center justify-content-center ${t.type === 'File' ? 'bg-primary bg-opacity-10 text-primary' : 'bg-success bg-opacity-10 text-success'}`} style={{width: 50, height: 50}}>
                            <i className={`bi ${t.type === 'File' ? 'bi-file-earmark-arrow-down' : 'bi-file-text'} fs-4`}></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-main">{t.name}</h5>
                            <span className="badge bg-input border border-theme text-muted-theme fw-normal">{t.category}</span>
                        </div>
                    </div>
                    {isSuperAdmin && (
                        <div className="dropdown">
                            <button className="btn btn-link text-muted-theme p-0" data-bs-toggle="dropdown"><i className="bi bi-three-dots-vertical"></i></button>
                            <ul className="dropdown-menu dropdown-menu-end bg-card border-theme">
                                <li><button className="dropdown-item text-main" onClick={() => openAddModal(t)}>Edit</button></li>
                                <li><button className="dropdown-item text-main" onClick={() => handleArchive(t.id, t.status)}>{t.status === 'Active' ? 'Archive' : 'Restore'}</button></li>
                                <li><hr className="dropdown-divider border-theme"/></li>
                                <li><button className="dropdown-item text-danger" onClick={() => handleDelete(t.id, t.name)}>Delete</button></li>
                            </ul>
                        </div>
                    )}
                  </div>

                  <div className="mt-auto">
                    {t.type === 'File' ? (
                        <a href={t.fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary w-100">
                            <i className="bi bi-download me-2"></i> Download File
                        </a>
                    ) : (
                        <button className="btn btn-outline-success w-100" onClick={() => openTextView(t)}>
                            <i className="bi bi-eye me-2"></i> View Text
                        </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ADD/EDIT MODAL (Super Admin Only) */}
      {showAddModal && isSuperAdmin && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-main">{isEditing ? "Edit Template" : "Add Template"}</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSave}>
                  <div className="mb-3">
                    <label className="small text-muted-theme">Template Name</label>
                    <input className="form-control" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                  </div>
                  
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                        <label className="small text-muted-theme">Type</label>
                        <select className="form-select" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                            <option value="File">File Upload</option>
                            <option value="Text">Text Content</option>
                        </select>
                    </div>
                    <div className="col-6">
                        <label className="small text-muted-theme">Category</label>
                        <select className="form-select" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                            <option>General</option>
                            <option>Contracts</option>
                            <option>Marketing</option>
                            <option>Emails</option>
                            <option>HR</option>
                        </select>
                    </div>
                  </div>

                  {formData.type === 'File' ? (
                    <div className="mb-3">
                        <label className="small text-muted-theme">Upload File</label>
                        <input type="file" className="form-control" onChange={handleFileChange} required={!isEditing} />
                        {isEditing && formData.fileUrl && (
                            <small className="d-block mt-1 text-accent">Current: {formData.fileName}</small>
                        )}
                    </div>
                  ) : (
                    <div className="mb-3">
                        <label className="small text-muted-theme">Text Content</label>
                        <textarea className="form-control" rows="6" required value={formData.textContent} onChange={e => setFormData({...formData, textContent: e.target.value})}></textarea>
                    </div>
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={uploading}>
                        {uploading ? "Saving..." : "Save Template"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW TEXT MODAL (All Users) */}
      {showTextModal && viewingTemplate && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-main">{viewingTemplate.name}</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <div className="bg-input p-3 rounded border border-theme mb-3">
                    <pre className="text-main mb-0" style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>{viewingTemplate.textContent}</pre>
                </div>
              </div>
              <div className="modal-footer border-theme">
                <button className="btn btn-primary" onClick={() => copyToClipboard(viewingTemplate.textContent)}>
                    <i className="bi bi-clipboard me-2"></i> Copy to Clipboard
                </button>
                <button className="btn btn-outline-secondary" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}